import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";
import type { PaginatedMeta } from "./audience_helper";

const apiBase = config.api.API_URL;

export type Campaign = {
  id: number;
  name: string;
  subject: string;
  html_body: string;
  editor_design: Record<string, unknown> | null;
  /** URLs meta (conversão leve) — F2. */
  goal_urls?: string[];
  /** Kit de marca (F2) — cores/rodapé herdam do tenant.brand_kit. */
  use_brand_kit?: boolean;
  /** ETL legado — badge na lista (LAC-114). */
  imported_from_legacy?: boolean;
  created_at?: string;
  updated_at?: string;
};

type CampaignsListResponse = ApiSuccess<Campaign[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

export type CampaignPayload = {
  name: string;
  subject: string;
  html_body: string;
  editor_design?: Record<string, unknown> | null;
  goal_urls?: string[];
  use_brand_kit?: boolean;
};

export const getCampaigns = (params?: {
  search?: string;
  /** true = só com HTML; false = só vazias; omitir = todas */
  has_html?: boolean;
  /** true = só importadas do legado; false = só nativas */
  imported_from_legacy?: boolean;
  page?: number;
  per_page?: number;
}): Promise<CampaignsListResponse> => {
  return axios.get(apiBase + "/campaigns", {
    params: {
      ...params,
      // Query string precisa de 0/1 — boolean puro some no axios às vezes.
      has_html:
        params?.has_html === undefined ? undefined : params.has_html ? 1 : 0,
      imported_from_legacy:
        params?.imported_from_legacy === undefined
          ? undefined
          : params.imported_from_legacy
            ? 1
            : 0,
    },
  });
};

export const getCampaign = (id: number): Promise<ApiSuccess<Campaign>> => {
  return axios.get(apiBase + "/campaigns/" + id);
};

export const createCampaign = (
  payload: CampaignPayload
): Promise<ApiSuccess<Campaign>> => {
  return axios.post(apiBase + "/campaigns", payload);
};

export type CampaignUpdatePayload = Partial<CampaignPayload> & {
  /** ISO do updated_at visto pelo cliente — 409 se divergir (optimistic lock). */
  expected_updated_at?: string | null;
};

export const updateCampaign = (
  id: number,
  payload: CampaignUpdatePayload
): Promise<ApiSuccess<Campaign>> => {
  return axios.patch(apiBase + "/campaigns/" + id, payload);
};

export const deleteCampaign = (id: number): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/campaigns/" + id);
};

export const duplicateCampaign = (
  id: number
): Promise<ApiSuccess<Campaign>> => {
  return axios.post(apiBase + "/campaigns/" + id + "/duplicate");
};

export const fetchCampaignHtmlFromUrl = (
  url: string
): Promise<ApiSuccess<{ html_body: string }>> => {
  return axios.post(apiBase + "/campaigns/fetch-html", { url });
};

export const saveCampaignAsTemplate = (
  id: number,
  payload?: { name?: string }
): Promise<
  ApiSuccess<{
    id: number;
    name: string;
    html_body: string;
    editor_design: Record<string, unknown> | null;
    created_at?: string;
    updated_at?: string;
  }>
> => {
  return axios.post(
    apiBase + "/campaigns/" + id + "/save-as-template",
    payload || {}
  );
};

export type PreviewMergeResult = {
  subject: string;
  html_body: string;
  mode: "contact" | "sample";
  contact: { id: number; name: string | null; email: string } | null;
  name_empty: boolean;
};

/** Prévia de personalização — contato real ou amostra (LAC-068). Sem SES/cota. */
export const previewCampaignMerge = (
  campaignId: number,
  payload?: {
    contact_id?: number | null;
    subject?: string;
    html_body?: string;
  }
): Promise<ApiSuccess<PreviewMergeResult>> => {
  return axios.post(
    apiBase + "/campaigns/" + campaignId + "/preview-merge",
    payload || {}
  );
};

/** Teto do editor — espelha CampaignAssetService::MAX_BYTES (5 MiB). */
export const CAMPAIGN_ASSET_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Upload de imagem do Easy Email — multipart `image` (LAC-031).
 * FormData: não forçar application/json (boundary do browser).
 */
export async function uploadCampaignAsset(
  file: Blob | File
): Promise<{ url: string; message?: string }> {
  const form = new FormData();
  const asFile =
    file instanceof File
      ? file
      : new File([file], guessImageFilename(file), {
          type: file.type || "image/png",
        });
  form.append("image", asFile);

  const envelope: any = await axios.post(apiBase + "/campaign-assets", form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData && headers) {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    ],
  });

  if (!envelope?.success || !envelope?.data?.url) {
    throw new Error(envelope?.error?.message || "campaign_asset_upload_failed");
  }

  return {
    url: String(envelope.data.url),
    message: envelope.message,
  };
}

function guessImageFilename(blob: Blob): string {
  const mime = (blob.type || "").toLowerCase();
  if (mime.includes("jpeg") || mime.includes("jpg")) return "image.jpg";
  if (mime.includes("webp")) return "image.webp";
  if (mime.includes("gif")) return "image.gif";
  return "image.png";
}
